import cn from 'classnames';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useSelector } from 'react-redux';
import PopUpContainer from 'src/components/common/modal/PopUp';
import { detectDevice, setBodyOverflow } from 'src/helpers/utils';
import { useConnectedAction } from 'src/hooks/use-connected-action';
import { useI18n } from 'src/hooks/use-i18n-translation';
import { getLoginPage } from 'src/services/cmsApi';
import { openAuthModal } from 'src/store/auth-modal/actions';
import { CmsLoginPageResponse } from 'src/types/cms/cmsPageTypes';
import { RootState } from 'src/types/store-types';
import { IModal } from '../../../../types/common-types/modal';
import { IForgotRes, ISignInRes, ISignUpRes } from '../../../../types/res-dto/authorization';
import ContentWithImage from './components/ContentWithImage';
import ContentWithoutImage from './components/ContentWithoutImage';
import ForgotPassContent from './components/ForgotPassContent';
import LoginContent from './components/LoginContent';
import './styles.scss';

interface IProps extends IModal {
  loginModalData: ISignInRes;
  forgotModalData: IForgotRes;
  signUpModalData: ISignUpRes;
  content: 'login' | 'forgot' | 'registration' | null;
  setModalContent: (action: 'open' | 'close', content: 'login' | 'registration' | 'forgot' | null) => void;
}

const AuthModal: FC<IProps> = ({
  visible,
  close,
  loginModalData,
  forgotModalData,
  signUpModalData,
  content,
  setModalContent,
  hasTitle,
}) => {
  const { locale } = useI18n();
  const [loginPage, setLoginPage] = useState<CmsLoginPageResponse | null>(null);
  const device = detectDevice();

  const _openAuthModal = useConnectedAction(openAuthModal);

  const { showMenu } = useSelector((state: RootState) => state.mobile);

  const [modalTitle, setModalTitle] = useState<string>('');
  const isPapi = process.env.REACT_APP_WEBSITENAME === 'PapiGames';

  const modalContentsMap = useMemo(
    () => ({
      login: (
        <LoginContent
          close={close}
          username={loginModalData.username}
          password={loginModalData.password}
          email={loginModalData.email}
          radio={loginModalData.radio}
          cpf={loginModalData.cpf}
          rememberMe={loginModalData.rememberMe}
          forgotLink={loginModalData?.forgotLink}
          signUp={loginModalData.signUp}
          setContent={setModalContent}
        />
      ),
      forgot: (
        <ForgotPassContent
          close={close}
          username={forgotModalData.username}
          email={forgotModalData.email}
          cpf={forgotModalData.cpf}
          signUp={forgotModalData.signUp}
          signIn={forgotModalData.signIn}
          setContent={setModalContent}
        />
      ),
    }),
    [loginModalData, forgotModalData, signUpModalData, content]
  );

  const variants: any = useMemo(
    () => ({
      login: loginModalData?.type,
      forgot: forgotModalData?.type,
      registration: signUpModalData?.type,
    }),
    [loginModalData, forgotModalData, signUpModalData]
  );

  const toggleModal = (): void => {
    !showMenu && setBodyOverflow('unset');
    _openAuthModal('login');
  };

  useEffect(() => {
    if (content === 'login') {
      getLoginPage(locale).then((res: any) => {
        if (res.success && res.result) {
          setLoginPage(res);
        }
      });
    }
  }, [content, locale]);

  useEffect(() => {
    if (hasTitle) {
      setModalTitle(content as string);
    }
  }, [content, hasTitle]);

  return (
    <>
      <HelmetProvider>
        <Helmet>
          <title>{loginPage?.result?.content?.meta?.pageTitle}</title>
          <meta name="description" content={loginPage?.result?.content?.meta?.pageDescription} />
          <meta name="keywords" content={loginPage?.result?.content?.meta?.pageKeywords} />
        </Helmet>
      </HelmetProvider>
      <PopUpContainer
        isOpen={visible}
        hasOverlay
        childerHasImage={variants[content as string]}
        type="withImage"
        handleClose={close}
        className={cn({ special_heading: isPapi && isMobile })}
      >
        {variants[content as string] === 'withImage' ? (
          <ContentWithImage
            modalContentsMap={modalContentsMap}
            content={content}
            loginPage={loginPage}
            device={device}
            modalTitle={modalTitle}
          />
        ) : (
          <ContentWithoutImage
            toggleModal={toggleModal}
            modalTitle={modalTitle}
            modalContentsMap={modalContentsMap}
            content={content}
          />
        )}
      </PopUpContainer>
    </>
  );
};

export default AuthModal;
